
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, SaveIcon, ThumbsUpIcon, ThumbsDownIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { SavedContent } from '../types';
import { trackEvent } from '../services/analyticsService';
import { Skeleton } from './ui/Skeleton';

interface StyleTipsModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string | null;
  isLoading: boolean;
  error: string | null;
  onRegenerate: () => void;
  onSaveContent: (contentToSave: Omit<SavedContent, 'id' | 'createdAt'>) => void;
  title: string;
  saveType: 'tips' | 'color_analysis' | 'plan';
  sourceImageUrl?: string;
}

const StyleTipsModal: React.FC<StyleTipsModalProps> = ({
  isOpen,
  onClose,
  content,
  isLoading,
  error,
  onRegenerate,
  onSaveContent,
  title,
  saveType,
  sourceImageUrl,
}) => {
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(false);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsSaved(false);
      setFeedback(null);
    }
  }, [isOpen, content]);

  const handleSave = () => {
    if (!content) return;
    const contentToSave: Omit<SavedContent, 'id' | 'createdAt'> = {
      type: saveType,
      title: title,
      content: content,
    };
    if (sourceImageUrl) {
      contentToSave.sourceImageUrl = sourceImageUrl;
    }
    onSaveContent(contentToSave);
    setIsSaved(true);
  };

  const handleFeedback = (type: 'up' | 'down') => {
    setFeedback(type);
    trackEvent('style_tips_feedback', { rating: type, saveType });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-gray-50 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl overflow-hidden dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100">{title}</h2>
              <button
                onClick={onClose}
                className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                aria-label={t('shareOutfitModal_close')}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-grow flex flex-col gap-6">
              {isLoading ? (
                <div className="flex flex-col gap-4 w-full">
                  <div className="flex items-center justify-between mb-2">
                    <Skeleton className="h-6 w-1/3" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-full mt-2" />
                  <Skeleton className="h-4 w-4/5" />
                  <div className="mt-4">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-4 animate-pulse">{t('styleTipsModal_loading')}</p>
                </div>
              ) : error ? (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md w-full dark:bg-red-900/20 dark:border-red-600 dark:text-red-300" role="alert">
                  <p className="font-bold">{t('error_title')}</p>
                  <p>{error}</p>
                </div>
              ) : content ? (
                <>
                  <div className="w-full prose dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: content }} />
                  <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-100 dark:border-gray-700/50 w-full">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Was this helpful?</span>
                    <button
                      onClick={() => handleFeedback('up')}
                      className={`p-1.5 rounded-full transition-colors ${feedback === 'up' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'}`}
                      title="Helpful"
                    >
                      <ThumbsUpIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleFeedback('down')}
                      className={`p-1.5 rounded-full transition-colors ${feedback === 'down' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'}`}
                      title="Not helpful"
                    >
                      <ThumbsDownIcon className="w-5 h-5" />
                    </button>
                    {feedback && <span className="text-xs text-indigo-600 dark:text-indigo-400 animate-fade-in">Thanks!</span>}
                  </div>
                </>
              ) : null}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              {content && !isLoading && !error && (
                <button
                  onClick={handleSave}
                  disabled={isSaved}
                  className="flex items-center justify-center bg-gray-700 text-white font-semibold py-2 px-5 rounded-lg hover:bg-gray-600 disabled:opacity-50"
                >
                  <SaveIcon className="w-5 h-5 mr-2" /> {isSaved ? t('myStylesPage_adviceSaved') : t('myStylesPage_saveAdvice')}
                </button>
              )}
              <button
                onClick={onRegenerate}
                disabled={isLoading}
                className="flex-shrink-0 bg-gray-900 text-white font-semibold py-2 px-5 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-700 active:scale-95 text-base disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-600 dark:hover:bg-indigo-500"
              >
                {t('styleTipsModal_getNewTips')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StyleTipsModal;
